import type { ZoneKpi } from "@/lib/api/agent";
import { cn } from "@/lib/utils";
import {
  formatZoneKpiRawValue,
  ZONE_KPI_AXIS_CAPTION,
  ZONE_KPI_NOT_READY_LABEL,
  ZONE_KPI_SCORE_MAX,
  ZONE_KPI_SCORE_MIN,
  type ZoneKpiKind,
} from "@/lib/zone-kpi";

function scorePercent(score: number): number {
  const span = ZONE_KPI_SCORE_MAX - ZONE_KPI_SCORE_MIN;
  return Math.min(100, Math.max(0, ((score - ZONE_KPI_SCORE_MIN) / span) * 100));
}

const SCALE_GRADIENT: Record<ZoneKpiKind, string> = {
  occupancy:
    "linear-gradient(to right, var(--info), var(--success), var(--info))",
  air_quality:
    "linear-gradient(to right, var(--warning), var(--success), var(--destructive))",
  temperature:
    "linear-gradient(to right, var(--info), var(--success), var(--warning))",
};

export function ZoneKpiMeter({
  kind,
  kpi,
  label,
}: {
  kind: ZoneKpiKind;
  kpi: ZoneKpi;
  label: string;
}) {
  const ready = kpi.ready;
  const captions = ZONE_KPI_AXIS_CAPTION[kind];
  const percent = ready && kpi.score != null ? scorePercent(kpi.score) : null;

  return (
    <div
      className="flex flex-col gap-1"
      data-kpi-kind={kind}
      data-kpi-ready={ready ? "true" : "false"}
      data-kpi-percent={percent ?? undefined}
    >
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-foreground/80">{label}</span>
        <span
          className={cn(
            "tabular-nums",
            ready ? "font-medium text-foreground" : "text-muted-foreground",
          )}
        >
          {formatZoneKpiRawValue(kind, kpi)}
        </span>
      </div>
      <div
        className="relative h-4"
        role="meter"
        aria-label={label}
        aria-valuemin={ZONE_KPI_SCORE_MIN}
        aria-valuemax={ZONE_KPI_SCORE_MAX}
        aria-valuenow={ready && kpi.score != null ? kpi.score : undefined}
        aria-valuetext={
          ready && kpi.score != null
            ? `${Math.round(kpi.score)} %`
            : ZONE_KPI_NOT_READY_LABEL
        }
      >
        <div
          className={cn(
            "absolute inset-x-0 top-1/2 h-2.5 -translate-y-1/2 rounded-full",
            ready ? "opacity-90" : "bg-muted-foreground/20",
          )}
          style={ready ? { backgroundImage: SCALE_GRADIENT[kind] } : undefined}
        />
        {percent != null && (
          <span
            data-kpi-thumb
            className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-foreground bg-card shadow-sm"
            style={{ left: `${percent}%` }}
            aria-hidden
          />
        )}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{captions.low}</span>
        <span>{captions.high}</span>
      </div>
    </div>
  );
}
